import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import { FormattedMessage } from 'react-intl';

import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Analytics } from '@trello/atlassian-analytics';
import { useOneTimeMessagesDismissed } from '@trello/business-logic-react/member';
import { useFeatureGate } from '@trello/feature-gate-client';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { Overlay } from '@trello/nachos/overlay';
import { RouteId, useRouteId } from '@trello/router';

import { tryBetaSpotlightSharedState } from '../PPBetaToolbox/tryBetaSpotlightSharedState';
import { useAssignPersonalProductivityCohort } from '../useAssignPersonalProductivityCohort';
import IconEyelashImagePngSrc from './icon-eyelash.png';
import PPOptInModalImagePngSrc from './pp-opt-in-modal-image.png';
import TrelloIconImagePngSrc from './trello-icon.png';

import styles from './PPOptInModal.less';

export const PP_OPT_IN_MODAL_MESSAGE = 'pp-opt-in-modal';

const noop = () => {};

export const PPOptInModal: FunctionComponent = () => {
  const { value: isGateEnabled } = useFeatureGate('trello_pp_opt_in_modal');
  const [isModalOpen, setIsModalOpen] = useState(true);
  const { dismissOneTimeMessage, isOneTimeMessageDismissed } =
    useOneTimeMessagesDismissed();
  const { assignPersonalProductivityBetaCohort } =
    useAssignPersonalProductivityCohort();
  const routeId = useRouteId();

  const isBoardRoute = routeId === RouteId.BOARD;

  const showModal = useMemo(
    () =>
      !isOneTimeMessageDismissed(PP_OPT_IN_MODAL_MESSAGE) &&
      isGateEnabled &&
      isModalOpen &&
      isBoardRoute,
    [isBoardRoute, isGateEnabled, isModalOpen, isOneTimeMessageDismissed],
  );

  const onClickNotNow = useCallback(async () => {
    setIsModalOpen(false);
    dismissOneTimeMessage(PP_OPT_IN_MODAL_MESSAGE);
    Analytics.sendClickedButtonEvent({
      buttonName: 'dismissPPOptInModalButton',
      source: 'ppOptInModal',
    });

    tryBetaSpotlightSharedState.setValue({
      isOpen: true,
    });
  }, [dismissOneTimeMessage]);

  const onClickTryBeta = useCallback(async () => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'enableBetaPPOptInModalButton',
      source: 'ppOptInModal',
    });

    try {
      await assignPersonalProductivityBetaCohort('opted_in');
    } catch (error) {
      showFlag({
        id: 'ppOptInModalError',
        title: 'Error opting into Beta',
        appearance: 'error',
      });
    }

    setIsModalOpen(false);
    dismissOneTimeMessage(PP_OPT_IN_MODAL_MESSAGE);
  }, [assignPersonalProductivityBetaCohort, dismissOneTimeMessage]);

  useEffect(() => {
    if (showModal) {
      Analytics.sendViewedComponentEvent({
        componentType: 'inlineDialog',
        componentName: 'ppOptInModal',
        source: 'boardScreen',
      });
    }
  }, [showModal]);

  return (
    showModal && (
      <Overlay
        className={styles.overlay}
        closeOnOutsideClick={false}
        onClose={noop}
      >
        <div className={styles.ppOptInModal}>
          <div className={styles.headingContainer}>
            <img
              className={styles.trelloIconEyelash}
              src={IconEyelashImagePngSrc}
              alt=""
            />
            <img src={TrelloIconImagePngSrc} alt="Trello" />
            <FormattedMessage
              id="templates.split_screen.onboarding.new-trello-modal.new-features"
              defaultMessage="New features to unleash your productivity"
              description="Beta opt-in modal heading"
              tagName="h1"
            />
          </div>
          <img
            className={styles.headingImage}
            src={PPOptInModalImagePngSrc}
            alt="Try the Beta Experience"
          />
          <div className={styles.contentContainer}>
            <FormattedMessage
              id="templates.split_screen.onboarding.new-trello-modal.with-our-new-inbox-and-planner"
              defaultMessage="With our new <b>Inbox</b> and <b>Planner</b>, you can capture to-dos from anywhere, organize them, and schedule time to get things done."
              description="Beta opt-in modal content"
              tagName="p"
              values={{ b: (chunks: ReactNode) => <b>{chunks}</b> }}
            />
            <FormattedMessage
              id="templates.split_screen.onboarding.new-trello-modal.take-a-quick-tour"
              defaultMessage="Take a quick tour and enable the beta experience."
              description="Beta opt-in modal content"
              tagName="p"
            />
            <div className={styles.ctasContainer}>
              <Button appearance="subtle" onClick={onClickNotNow}>
                <FormattedMessage
                  id="templates.split_screen.onboarding.new-trello-modal.not-now"
                  defaultMessage="Not now"
                  description="Beta opt-in modal deferral button"
                />
              </Button>
              <Button
                appearance="primary"
                className={styles.enabledBeta}
                onClick={onClickTryBeta}
              >
                <FormattedMessage
                  id="templates.split_screen.onboarding.new-trello-modal.get-started"
                  defaultMessage="Get started"
                  description="Beta opt-in modal enable beta button"
                />
                <ArrowRightIcon
                  aria-hidden="true"
                  color={'currentColor'}
                  label=""
                />
              </Button>
            </div>
          </div>
        </div>
      </Overlay>
    )
  );
};
